import { useQuery } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import { useCreatorMe } from '../../../lib/auth';
import { UploadWidget } from './UploadWidget';
import { Composer, Thread } from './MessagesView';
import styles from './Hub.module.css';

const VIDEO_STATUS_CFG: Record<string, { label: string; cls: string }> = {
  awaiting_submission: { label: 'Awaiting Submission', cls: styles.badgeGray },
  under_review: { label: 'Under Review', cls: styles.badgeBlue },
  revisions_requested: { label: 'Revisions Requested', cls: styles.badgeOrange },
  approved: { label: 'Approved', cls: styles.badgeGreen },
};

export function VideoSubmissionView() {
  const { data } = useCreatorMe();
  const draft1 = useQuery({
    queryKey: ['creator', 'draft-url', 1],
    queryFn: () => api.get<{ url: string }>(`/api/creator/drafts/1/url`),
    enabled: !!data?.draft_1_url,
    staleTime: 30 * 60_000,
  });
  const draft2 = useQuery({
    queryKey: ['creator', 'draft-url', 2],
    queryFn: () => api.get<{ url: string }>(`/api/creator/drafts/2/url`),
    enabled: !!data?.draft_2_url,
    staleTime: 30 * 60_000,
  });
  if (!data) return null;

  const statusKey = data.video_status || 'awaiting_submission';
  const cfg = VIDEO_STATUS_CFG[statusKey] ?? VIDEO_STATUS_CFG.awaiting_submission;
  const showDraft2Upload = statusKey === 'revisions_requested';

  return (
    <>
      <div className={styles.pageActionRow} style={{ marginBottom: 20 }}>
        <div>
          <div className={styles.pageTitle}>Video Submission</div>
          <div className={styles.pageSubtitle}>
            Upload your content and track its review status here.
          </div>
        </div>
        <span className={`${styles.badge} ${cfg.cls}`}>{cfg.label}</span>
      </div>
      <div className={styles.vsLayout}>
        <div className={styles.vsColLeft}>
          <div className={styles.card}>
            <div className={styles.cardHead}>
              <div className={styles.cardSectionTitle}>Draft 1</div>
              {data.draft_1_url && (
                <span className={`${styles.badge} ${styles.badgeGreen}`}>Submitted</span>
              )}
            </div>
            <div className={styles.cardBody}>
              {data.draft_1_url && draft1.data?.url ? (
                <video className={styles.vsVideo} controls src={draft1.data.url} />
              ) : (
                <UploadWidget draftNum={1} title="Upload Your Video" />
              )}
            </div>
          </div>
          {(data.draft_2_url || showDraft2Upload) && (
            <div className={styles.card}>
              <div className={styles.cardHead}>
                <div className={styles.cardSectionTitle}>Draft 2</div>
                {showDraft2Upload && !data.draft_2_url && (
                  <span className={`${styles.badge} ${styles.badgeOrange}`}>
                    Revisions requested
                  </span>
                )}
                {data.draft_2_url && (
                  <span className={`${styles.badge} ${styles.badgeGreen}`}>Submitted</span>
                )}
              </div>
              <div className={styles.cardBody}>
                {data.draft_2_url && draft2.data?.url ? (
                  <video className={styles.vsVideo} controls src={draft2.data.url} />
                ) : (
                  <UploadWidget draftNum={2} title="Upload Your Revised Video" />
                )}
              </div>
            </div>
          )}
          <div className={styles.card}>
            <div className={styles.cardHead}>
              <div className={styles.cardSectionTitle}>AI Review</div>
              <span
                className={`${styles.badge} ${styles.badgeGray}`}
                style={{ fontSize: 10, padding: '2px 7px' }}
              >
                Coming soon
              </span>
            </div>
            <div className={styles.cardBody}>
              <div
                style={{
                  fontSize: 14,
                  color: '#6b7280',
                  fontStyle: 'italic',
                  textAlign: 'center',
                  padding: '24px 0 8px',
                }}
              >
                Placeholder — AI transcript review coming soon.
              </div>
            </div>
          </div>
        </div>
        <div className={styles.vsColRight}>
          <div className={styles.card}>
            <div className={styles.cardHead}>
              <div className={styles.cardSectionTitle}>Messages</div>
            </div>
            <Thread />
            <Composer />
          </div>
        </div>
      </div>
    </>
  );
}
